// A shared, framework-agnostic waypoint-patrol primitive: an authored list of
// points and a loop mode (the shape is `WaypointPatrol`, defined alongside
// the rest of the persisted schema in `content/schema/map.ts` since it's
// authored data), advanced by elapsed time the same way sprite animation
// already is (`spriteAnimationCells`/`stepAnimationClock` in
// `runtime/twod/sprite2d.ts`/`npc2d.ts`) - "which frame plays at this elapsed
// time" becomes "where is the patroller at this elapsed time". Used by both
// the 2D NPC runtime (`runtime/twod/npc2d.ts`) and placed 3D characters, so it
// lives here rather than under `runtime/twod/`.
//
// No pathfinding: legs between waypoints are straight lines, and collision
// against static geometry is a placement-time authoring concern, not
// something this module solves (see
// docs/game-maker-completion/design.md section 5).
import type { PatrolLoopMode, PatrolWaypoint, WaypointPatrol } from '../content/schema/map';

export type { PatrolLoopMode, PatrolWaypoint, WaypointPatrol };

export interface PatrolVector {
  x: number;
  y: number;
  z: number;
}

export interface PatrolSample {
  position: PatrolVector;
  /** Unit direction of this waypoint's outgoing leg: the one in progress
   * while travelling, or the upcoming one a dwelling patroller is about to
   * depart on (so a dwelling NPC faces the way it is about to walk, not the
   * way it arrived) - a zero vector when that leg's two waypoints coincide.
   * A renderer derives its own facing/heading convention from this rather
   * than the primitive assuming one. */
  direction: PatrolVector;
  /** Index into `points` of the waypoint this sample is departing from
   * (dwelling there, or already travelling away from it). */
  waypointIndex: number;
  /** True while holding position at a waypoint's `dwellMs`. */
  dwelling: boolean;
  /** True only for `loopMode: 'once'`, once the final waypoint is reached -
   * `position` then stays parked there for any later elapsed time. */
  finished: boolean;
}

/** A patrol's own advancing clock - the same shape as `AnimationClock`, but
 * with no "name changed, reset to 0" transition to make: a patrol always
 * keeps accumulating, and `patrolSampleAt` derives looping, dwelling and
 * finishing from the running total rather than from clock resets. */
export interface PatrolClock {
  elapsedMs: number;
}

export const INITIAL_PATROL_CLOCK: PatrolClock = { elapsedMs: 0 };

/** Advance a patrol clock by one frame. */
export function stepPatrolClock(prev: PatrolClock, dtSeconds: number): PatrolClock {
  return { elapsedMs: prev.elapsedMs + dtSeconds * 1000 };
}

/** A speed of 0 (or authored negative) would make any real distance take an
 * infinite time to cross; clamped to a small positive floor instead so a
 * patrol with a zero-ish speed reads as "extremely slow" (frozen for any
 * realistic play session) rather than producing `Infinity`/`NaN` positions. */
const MIN_PATROL_SPEED = 1e-4;

function vecAt(p: PatrolWaypoint): PatrolVector {
  return { x: p.x, y: p.y, z: p.z ?? 0 };
}

function distance(a: PatrolWaypoint, b: PatrolWaypoint): number {
  return Math.hypot(b.x - a.x, b.y - a.y, (b.z ?? 0) - (a.z ?? 0));
}

function direction(a: PatrolWaypoint, b: PatrolWaypoint): PatrolVector {
  const d = distance(a, b);
  if (d === 0) return { x: 0, y: 0, z: 0 };
  return { x: (b.x - a.x) / d, y: (b.y - a.y) / d, z: ((b.z ?? 0) - (a.z ?? 0)) / d };
}

/** The ordered [from, to] waypoint-index pairs one full cycle visits, before
 * any dwell/travel timing is attached - `loop` wraps the last point back to
 * the first, `once` stops after the last point, `ping-pong` walks the list
 * forward then back (repeating, not stopping after one bounce). Fewer than
 * two points has no leg to walk. */
function legOrder(loopMode: PatrolLoopMode, count: number): [number, number][] {
  if (count < 2) return [];
  if (loopMode === 'once') {
    return Array.from({ length: count - 1 }, (_, i) => [i, i + 1]);
  }
  if (loopMode === 'loop') {
    return Array.from({ length: count }, (_, i) => [i, (i + 1) % count]);
  }
  const forward: [number, number][] = Array.from({ length: count - 1 }, (_, i) => [i, i + 1]);
  const backward: [number, number][] = Array.from({ length: count - 1 }, (_, i) => [count - 1 - i, count - 2 - i]);
  return [...forward, ...backward];
}

interface PatrolSegment {
  kind: 'dwell' | 'travel';
  fromIndex: number;
  toIndex: number;
  durationMs: number;
}

/** One full cycle's timeline: each leg becomes a dwell (at its `from`
 * waypoint) followed by the travel to its `to` waypoint, so `patrolSampleAt`
 * can walk a flat list of durations instead of re-deriving legs per sample. */
function buildTimeline(patrol: WaypointPatrol): PatrolSegment[] {
  const legs = legOrder(patrol.loopMode, patrol.points.length);
  const speed = patrol.speed > 0 ? patrol.speed : MIN_PATROL_SPEED;
  return legs.flatMap(([from, to]) => {
    const dwellMs = Math.max(0, patrol.points[from]!.dwellMs ?? 0);
    const travelMs = (distance(patrol.points[from]!, patrol.points[to]!) / speed) * 1000;
    return [
      { kind: 'dwell' as const, fromIndex: from, toIndex: to, durationMs: dwellMs },
      { kind: 'travel' as const, fromIndex: from, toIndex: to, durationMs: travelMs },
    ];
  });
}

/**
 * `patrolSampleAt` is called from `useFrame` - once per patrolling NPC or
 * character, every rendered frame - so rebuilding `buildTimeline`'s arrays
 * from scratch each call would mean real per-frame GC churn once more than a
 * few patrols are on screen at once. Cached by the `WaypointPatrol` object's
 * own identity, the same "an edited object is a new reference, so it simply
 * gets its own fresh entry" convention `characterClip.ts`'s `clipCache`
 * already uses for the equivalent per-clip derived data.
 */
const timelineCache = new WeakMap<WaypointPatrol, { timeline: PatrolSegment[]; cycleMs: number }>();

function getTimeline(patrol: WaypointPatrol): { timeline: PatrolSegment[]; cycleMs: number } {
  const cached = timelineCache.get(patrol);
  if (cached) return cached;
  const timeline = buildTimeline(patrol);
  const entry = { timeline, cycleMs: timeline.reduce((sum, s) => sum + s.durationMs, 0) };
  timelineCache.set(patrol, entry);
  return entry;
}

/** Where a patrol is at `elapsedMs` since it started - a pure function of
 * elapsed time, the same "which frame plays now" shape `spriteAnimationCells`
 * already gives sprite playback. A caller needs no bespoke per-frame stepping
 * code of its own: accumulate elapsed time in a `PatrolClock` exactly like an
 * `AnimationClock` already does (`stepPatrolClock`), then call this. */
export function patrolSampleAt(patrol: WaypointPatrol, elapsedMs: number): PatrolSample {
  const { points } = patrol;
  if (points.length === 0) {
    return { position: { x: 0, y: 0, z: 0 }, direction: { x: 0, y: 0, z: 0 }, waypointIndex: -1, dwelling: false, finished: true };
  }
  if (points.length === 1) {
    return { position: vecAt(points[0]!), direction: { x: 0, y: 0, z: 0 }, waypointIndex: 0, dwelling: true, finished: patrol.loopMode === 'once' };
  }

  const { timeline, cycleMs } = getTimeline(patrol);
  const lastLegTo = timeline[timeline.length - 1]!.toIndex;

  if (patrol.loopMode === 'once' && elapsedMs >= cycleMs) {
    return { position: vecAt(points[lastLegTo]!), direction: { x: 0, y: 0, z: 0 }, waypointIndex: lastLegTo, dwelling: true, finished: true };
  }
  if (cycleMs <= 0) {
    return { position: vecAt(points[lastLegTo]!), direction: { x: 0, y: 0, z: 0 }, waypointIndex: lastLegTo, dwelling: true, finished: patrol.loopMode === 'once' };
  }

  const t = patrol.loopMode === 'once' ? elapsedMs : ((elapsedMs % cycleMs) + cycleMs) % cycleMs;

  let acc = 0;
  for (let i = 0; i < timeline.length; i++) {
    const seg = timeline[i]!;
    const end = acc + seg.durationMs;
    if (t < end || i === timeline.length - 1) {
      const from = points[seg.fromIndex]!;
      const to = points[seg.toIndex]!;
      const dir = direction(from, to);
      if (seg.kind === 'dwell') {
        return { position: vecAt(from), direction: dir, waypointIndex: seg.fromIndex, dwelling: true, finished: false };
      }
      const local = t - acc;
      const frac = seg.durationMs > 0 ? Math.min(1, Math.max(0, local / seg.durationMs)) : 1;
      return {
        position: {
          x: from.x + (to.x - from.x) * frac,
          y: from.y + (to.y - from.y) * frac,
          z: (from.z ?? 0) + ((to.z ?? 0) - (from.z ?? 0)) * frac,
        },
        direction: dir,
        waypointIndex: seg.fromIndex,
        dwelling: false,
        finished: false,
      };
    }
    acc = end;
  }
  // Unreachable: the loop above always returns on its final iteration.
  return { position: vecAt(points[0]!), direction: { x: 0, y: 0, z: 0 }, waypointIndex: 0, dwelling: true, finished: false };
}
